import { useCallback, useEffect, useRef, useState } from 'react';

import CustomImage from '@/components/CustomImage/CustomImage';

export interface TripImportSourcePreviewPanelProps {
  imageUrl: string;
  fallbackImageUrls?: string[];
  platform?: 'instagram' | 'tiktok';
}

export function TripImportSourcePreviewPanel({
  imageUrl,
  fallbackImageUrls = [],
  platform = 'instagram',
}: TripImportSourcePreviewPanelProps) {
  const platformLabel = platform === 'tiktok' ? 'TikTok' : 'Instagram';
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPreviewLoaded(false);
    let cancelled = false;
    const syncIfCached = () => {
      const img = frameRef.current?.querySelector('img');
      if (!cancelled && img?.complete && img.naturalWidth > 0) {
        setPreviewLoaded(true);
      }
    };
    syncIfCached();
    const t1 = window.setTimeout(syncIfCached, 50);
    const t2 = window.setTimeout(syncIfCached, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [imageUrl]);

  const handlePreviewLoad = useCallback(() => {
    setPreviewLoaded(true);
  }, []);

  return (
    <aside
      className="trip-import-source-preview hidden min-h-0 w-[360px] shrink-0 flex-col items-center justify-center border-l border-[#e1e1e7] bg-[#fafafa] min-[1140px]:flex"
      aria-label={`${platformLabel} post preview`}
    >
      <div className="relative mx-auto mt-8 h-[529px] w-[252px] shrink-0 rounded-[36px] border-[3px] border-[#525257] bg-black p-[6px] shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <div ref={frameRef} className="relative h-full w-full overflow-hidden rounded-[30px] bg-[#111]">
          {!previewLoaded ? (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-[#111]"
              aria-busy="true"
              aria-live="polite"
            >
              <span
                className="inline-flex h-8 w-8 animate-spin rounded-full border-2 border-[#525257] border-t-[#ff5212]"
                aria-hidden
              />
              <span className="font-manrope text-[11px] leading-4 tracking-[0.22px] text-[#a7a7ae]">
                Loading preview…
              </span>
            </div>
          ) : null}
          <CustomImage
            src={imageUrl}
            fallbackSources={fallbackImageUrls}
            alt=""
            fill
            lazy={false}
            sizes="234px"
            fallbackSrc="/images/default_post.png"
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            onLoad={handlePreviewLoad}
          />
        </div>
      </div>
      <p className="mt-4 px-6 text-center font-manrope text-[12px] leading-4 tracking-[0.24px] text-[#525257]">
        {platformLabel} preview
      </p>
    </aside>
  );
}
